import { InputHTMLAttributes, forwardRef } from 'react';
import { classNames } from '@/utils/classNames';

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary';
  onChange?: ((checked: boolean) => void) | ((event: React.ChangeEvent<HTMLInputElement>) => void);
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { label, description, error, size = 'md', variant = 'default', className, onChange, ...props },
    ref
  ) => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const textSizeClasses = {
      sm: 'text-sm',
      md: 'text-sm',
      lg: 'text-base',
    };

    const variantClasses = {
      default: 'text-primary-600 focus:ring-primary-500',
      primary: 'text-primary-600 focus:ring-primary-500',
    };

    const checkboxClasses = classNames(
      'rounded border-gray-300 dark:border-gray-600',
      'bg-white dark:bg-gray-700',
      'transition-colors duration-200',
      'focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800',
      'touch-target cursor-pointer',
      sizeClasses[size],
      variantClasses[variant],
      error && 'border-red-300 dark:border-red-600 focus:ring-red-500',
      props.disabled && 'opacity-50 cursor-not-allowed',
      className
    );

    const ContainerComponent = label || description ? 'label' : 'div';

    return (
      <div className='space-y-1'>
        <ContainerComponent
          className={classNames(
            'flex items-start space-x-3',
            (label || description) && 'cursor-pointer touch-target',
            props.disabled && 'cursor-not-allowed'
          )}
        >
          <input
            ref={ref}
            type='checkbox'
            className={checkboxClasses}
            onChange={e => {
              // 互換性のため、boolean引数とevent引数両方に対応
              if (onChange) {
                if (onChange.length === 1) {
                  // boolean引数を期待する関数
                  (onChange as (checked: boolean) => void)(e.target.checked);
                } else {
                  // event引数を期待する関数
                  (onChange as (event: React.ChangeEvent<HTMLInputElement>) => void)(e);
                }
              }
            }}
            {...props}
          />
          {(label || description) && (
            <div className='flex-1 min-w-0'>
              {label && (
                <div
                  className={classNames(
                    'font-medium',
                    textSizeClasses[size],
                    error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
                    props.disabled && 'text-gray-500 dark:text-gray-500'
                  )}
                >
                  {label}
                </div>
              )}
              {description && (
                <p
                  className={classNames(
                    'text-sm text-gray-500 dark:text-gray-400',
                    label && 'mt-1',
                    props.disabled && 'text-gray-400 dark:text-gray-600'
                  )}
                >
                  {description}
                </p>
              )}
            </div>
          )}
        </ContainerComponent>
        {error && <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
